// Target: src/modules/billing/utils/billingSettingsFormUtils.ts
import * as yup from "yup";

import type {
	PaymentTargetDaysInputFormData,
} from "~/components/formElements/PaymentTargetInput/paymentTargetDaysInputTypes.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { getPaymentTargetDefaultValues } from "~/utils/form/formUtils.ts";

export type AdditionalEmailRecipient = {
	email: string;
	name: string;
};

export type ParsedBillingSettings = Omit<BillingSettings, "additionalEmailRecipients"> & {
	additionalEmailRecipients: AdditionalEmailRecipient[];
};

const EMAIL_ENTRY_PATTERN = /(?:"([^"]*)"\s*)?<([^>]+)>|(\S+@\S+)/g;

export function parseAdditionalEmailRecipients(value: string | null): AdditionalEmailRecipient[] {
	if (!value) return [];

	const recipients: AdditionalEmailRecipient[] = [];
	for (const match of value.matchAll(EMAIL_ENTRY_PATTERN)) {
		const name = match[1] ?? "";
		const email = match[2] ?? match[3] ?? "";
		if (email) {
			recipients.push({ email, name });
		}
	}
	return recipients;
}

export function serializeAdditionalEmailRecipients(recipients: AdditionalEmailRecipient[]): string {
	return recipients
		.filter(({ email }) => email)
		.map(({ email, name }) => (name ? `"${name}" <${email}>` : email))
		.join(", ");
}

export function parseBillingSettings(settings: BillingSettings): ParsedBillingSettings {
	return {
		...settings,
		additionalEmailRecipients: parseAdditionalEmailRecipients(settings.additionalEmailRecipients),
	};
}

export interface BillingSettingsBaseFormData extends PaymentTargetDaysInputFormData {
	additionalEmailRecipients: AdditionalEmailRecipient[];
	billingInstructions: string | null;
	clientContactPersonId: string | null;
	currencyId: string;
	invoiceByMail: boolean;
	invoiceRecipientId: string | null;
	invoiceTypeId: string;
	isBilledMonthly: boolean;
	isFixedPrice: boolean;
	isForeignCountry: boolean;
	isSkillBased: boolean;
	travelExpensesIncluded: boolean;
}

export interface ProjectBillingSettingsFormData extends BillingSettingsBaseFormData {
	combineOrders: boolean;
}

export const RECIPIENT_VALIDATION_MESSAGE = "Bitte wähle einen Kontakt oder zentralen Rechnungsempfänger aus.";

export function recipientValidationTest(value: Record<string, unknown>): boolean {
	return Boolean(value.clientContactPersonId || value.invoiceRecipientId);
}

export function createBillingSettingsBaseSchema() {
	return yup
		.object({
			additionalEmailRecipients: yup.array().of(
				yup.object({
					email: yup.string().email("Bitte gib eine gültige E-Mail-Adresse ein.").required("E-Mail ist erforderlich."),
					name: yup.string().default(""),
				}),
			).default([]),
			billingInstructions: yup.string().nullable().default(""),
			clientContactPersonId: yup.string().nullable().default(null),
			currencyId: yup.string().required(),
			invoiceByMail: yup.boolean().required(),
			invoiceRecipientId: yup.string().nullable().default(null),
			invoiceTypeId: yup.string().required(),
			isBilledMonthly: yup.boolean().required(),
			isFixedPrice: yup.boolean().required(),
			isForeignCountry: yup.boolean().required(),
			isSkillBased: yup.boolean().required(),
			paymentTargetDays: yup.number().required(),
			paymentTargetDaysCustom: yup
				.number()
				.nullable()
				.transform((value, original) => (original === "" ? null : value || null))
				.when("paymentTargetDays", (field1Value: number[], schema) => {
					return field1Value[0] === 0 ? schema.required() : schema.nullable();
				}),
			travelExpensesIncluded: yup.boolean().required(),
		})
		.test("atLeastOneRecipient", RECIPIENT_VALIDATION_MESSAGE, recipientValidationTest);
}

export function createProjectBillingSettingsSchema() {
	return createBillingSettingsBaseSchema().shape({
		combineOrders: yup.boolean().required(),
	});
}

export function getBillingSettingsBaseDefaultValues(settings: BillingSettings): BillingSettingsBaseFormData {
	const { paymentTargetDays, paymentTargetDaysCustom } = getPaymentTargetDefaultValues(
		settings.paymentTargetDays,
	);

	return {
		additionalEmailRecipients: parseAdditionalEmailRecipients(settings.additionalEmailRecipients),
		billingInstructions: settings.billingInstructions || "",
		clientContactPersonId: settings.clientContactPersonId || null,
		currencyId: settings.currencyId,
		invoiceByMail: settings.invoiceByMail,
		invoiceRecipientId: settings.invoiceRecipientId || null,
		invoiceTypeId: settings.invoiceTypeId,
		isBilledMonthly: settings.isBilledMonthly,
		isFixedPrice: settings.isFixedPrice,
		isForeignCountry: settings.isForeignCountry,
		isSkillBased: settings.isSkillBased,
		paymentTargetDays,
		paymentTargetDaysCustom,
		travelExpensesIncluded: settings.travelExpensesIncluded,
	};
}

export function getProjectBillingSettingsDefaultValues(settings: BillingSettings): ProjectBillingSettingsFormData {
	return {
		...getBillingSettingsBaseDefaultValues(settings),
		combineOrders: !settings.billOrdersSeparately,
	};
}

// --- Payload conversion (form data → API) ---
export type OrderBillingSettingsRegularInvoicePayload = Omit<BillingSettingsBaseFormData,
	"additionalEmailRecipients" | "paymentTargetDaysCustom"> & {
	additionalEmailRecipients: string | null;
};

export type OrderBillingSettingsSelfBilledPayload = Pick<OrderBillingSettingsRegularInvoicePayload,
	"invoiceTypeId" | "invoiceRecipientId" | "clientContactPersonId"> & {
	billOrdersSeparately: boolean;
};

export type ProjectBillingSettingsSelfBilledPayload = OrderBillingSettingsSelfBilledPayload & {
	billOrdersSeparately: boolean;
};

export type ProjectBillingSettingsRegularPayload = OrderBillingSettingsRegularInvoicePayload & {
	billOrdersSeparately: boolean;
};

export type ProjectBillingSettingsPayload =
	ProjectBillingSettingsRegularPayload
	| ProjectBillingSettingsSelfBilledPayload;

export type OrderBillingSettingsPayload =
	OrderBillingSettingsRegularInvoicePayload
	| OrderBillingSettingsSelfBilledPayload;

function prepareBillingSettingsBasePayload(
	formData: BillingSettingsBaseFormData,
): OrderBillingSettingsRegularInvoicePayload {
	const { additionalEmailRecipients, paymentTargetDaysCustom, ...rest } = formData;
	return {
		...rest,
		additionalEmailRecipients:
			additionalEmailRecipients.length > 0
				? serializeAdditionalEmailRecipients(additionalEmailRecipients)
				: null,
		paymentTargetDays:
			formData.paymentTargetDays === 0
				? paymentTargetDaysCustom ?? 0
				: formData.paymentTargetDays,
	};
}

export function prepareProjectBillingSettingsPayload(
	formData: ProjectBillingSettingsFormData,
): ProjectBillingSettingsPayload {
	const { combineOrders, ...baseFormData } = formData;
	const billOrdersSeparately = !combineOrders;
	if (formData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice) {
		return {
			invoiceTypeId: formData.invoiceTypeId,
			invoiceRecipientId: formData.invoiceRecipientId,
			clientContactPersonId: formData.clientContactPersonId,
			billingInstructions: formData.billingInstructions,
			billOrdersSeparately,
		};
	}

	return {
		...prepareBillingSettingsBasePayload(baseFormData),
		billOrdersSeparately,
	};
}

export function prepareOrderBillingSettingsPayload(
	formData: BillingSettingsBaseFormData,
): OrderBillingSettingsPayload {
	if (formData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice) {
		return {
			invoiceTypeId: formData.invoiceTypeId,
			invoiceRecipientId: formData.invoiceRecipientId,
			clientContactPersonId: formData.clientContactPersonId,
			billingInstructions: formData.billingInstructions,
			billOrdersSeparately: false,
		};
	}

	return prepareBillingSettingsBasePayload(formData);
}

